import { Button, ConnectivityBanner } from "@games-monorepo/shared-ui";
import {
  DEFAULT_YAK_TRAILS_CONFIG,
  DEFAULT_YAK_TRAILS_CONFIG_HASH,
  YAK_TRAILS_PROTOCOL_VERSION,
  YAK_TRAILS_SKINS,
  type BootstrapResult,
  type GameMode,
  type PartySummary,
  type RoomConnection,
  type RoomEvent,
  type RoomTransport,
  type RunResult,
  type SessionKind,
  type SkinDefinition,
  type PublicTournamentStanding,
  type PublicTournamentBracket,
  type TournamentSummary,
} from "@games-monorepo/yak-trails-core";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { ApiSessionApi } from "./api/ApiSessionApi";
import {
  GameFeedbackController,
  type GameFeedbackPreferences,
} from "./client/gameFeedback";
import { spectatorActionState } from "./client/spectatorActions";
import {
  cycleSpectatorTargetId,
  resolveSpectatorTargetId,
} from "./client/spectatorTargets";
import { useGameViewport } from "./client/useGameViewport";
import { useSteeringControls } from "./client/useSteeringControls";
import { YAK_TRAILS_API_URL, YAK_TRAILS_ONLINE_ENABLED } from "./clientConfig";
import { applyTerritoryPatch, resultFromRoomEvent, type TerritoryState, type YakTrailsProps, type YakTrailsScreen } from "./model";
import { ArenaScene } from "./rendering/ArenaScene";
import { MiniMap } from "./rendering/MiniMap";
import { SkinShowcase } from "./rendering/SkinShowcase";
import { LocalPracticeTransport } from "./transports/LocalPracticeTransport";
import { WebSocketRoomTransport } from "./transports/WebSocketRoomTransport";
import { YAK_TRAILS_CLIENT_TUNING } from "./tuning";
import { FirstRunTutorial } from "./tutorial/FirstRunTutorial";
import { TournamentAdminConsole } from "./tournaments/TournamentAdminConsole";
import { TournamentBracket } from "./tournaments/TournamentBracket";

const GRID_SIZE = DEFAULT_YAK_TRAILS_CONFIG.world.gridSize;
const EMPTY_TERRITORY: TerritoryState = {
  owners: new Array<string | null>(GRID_SIZE * GRID_SIZE).fill(null),
  revision: 0,
};

type ConnectionStatus = "idle" | "bootstrapping" | "matching" | "reconnecting";

function readLocalSkin(): string {
  try {
    const skinId = localStorage.getItem("yak-trails:selected-skin");
    return YAK_TRAILS_SKINS.some((skin) => skin.id === skinId && skin.starter) ? skinId! : "yak";
  } catch {
    return "yak";
  }
}

function writeLocalSkin(skinId: string): void {
  try {
    localStorage.setItem("yak-trails:selected-skin", skinId);
  } catch {
    // Storage can be disabled in private browsing; selection still works in-memory.
  }
}

function savePracticeBest(result: RunResult): void {
  try {
    const previous = Number.parseFloat(localStorage.getItem("yak-trails:practice-best") ?? "0");
    if (result.peakTerritoryPercent > previous) {
      localStorage.setItem("yak-trails:practice-best", result.peakTerritoryPercent.toFixed(2));
    }
  } catch {
    // Practice is intentionally usable without persistent storage.
  }
}

function readTutorialComplete(): boolean {
  try {
    return localStorage.getItem("yak-trails:tutorial-complete") === "1";
  } catch {
    return false;
  }
}

function writeTutorialComplete(): void {
  try {
    localStorage.setItem("yak-trails:tutorial-complete", "1");
  } catch {
    // The tutorial remains replayable when private browsing disables storage.
  }
}

function classNames(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(" ");
}

function initialRenderQuality(): "high" | "low" {
  if (typeof window === "undefined") return "high";
  return window.innerWidth <= YAK_TRAILS_CLIENT_TUNING.quality.mobileWidthBreakpoint
    || window.devicePixelRatio > YAK_TRAILS_CLIENT_TUNING.quality.highDensityCutoff
    ? "low"
    : "high";
}

function tournamentStartLabel(startsAt: string): string {
  const value = new Date(startsAt);
  if (Number.isNaN(value.getTime())) return "Schedule pending";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(value);
}

function MenuTile({
  emoji,
  title,
  detail,
  onClick,
  disabled = false,
  accent = "mint",
}: {
  emoji: string;
  title: string;
  detail: string;
  onClick: () => void;
  disabled?: boolean;
  accent?: "mint" | "sun" | "berry" | "violet";
}) {
  return (
    <button
      type="button"
      className={`yak-trails__menu-tile yak-trails__menu-tile--${accent}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="yak-trails__menu-emoji" aria-hidden="true">{emoji}</span>
      <span>
        <strong>{title}</strong>
        <small>{detail}</small>
      </span>
      <span className="yak-trails__menu-arrow" aria-hidden="true">›</span>
    </button>
  );
}

function ScreenHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <header className="yak-trails__screen-header">
      <button type="button" className="yak-trails__icon-button" onClick={onBack} aria-label="Go back">←</button>
      <h2>{title}</h2>
      <span className="yak-trails__header-spacer" />
    </header>
  );
}

function LoadingPill({ label }: { label: string }) {
  return (
    <div className="yak-trails__loading-pill" role="status">
      <span className="yak-trails__spinner" />
      {label}
    </div>
  );
}

export function YakTrails({
  sessionApi: suppliedSessionApi,
  practiceEnabled = true,
  onlineEnabled = YAK_TRAILS_ONLINE_ENABLED,
  initialPartySlug,
  initialTournamentId,
  initialAdminScreen,
  onBack,
  className,
  practiceWorkerUrl,
  territoryWorkerUrl,
  onTelemetry,
}: YakTrailsProps) {
  const defaultSessionApi = useMemo(() => new ApiSessionApi(YAK_TRAILS_API_URL), []);
  const sessionApi = suppliedSessionApi ?? defaultSessionApi;
  const [screen, setScreen] = useState<YakTrailsScreen>(
    initialPartySlug || initialTournamentId || initialAdminScreen ? "connecting" : "home",
  );
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(onlineEnabled ? "bootstrapping" : "idle");
  const [isStarting, setIsStarting] = useState(false);
  const [bootstrap, setBootstrap] = useState<BootstrapResult>({
    skins: YAK_TRAILS_SKINS,
    onlineAvailable: false,
    schoolAvailable: false,
  });
  const [selectedSkinId, setSelectedSkinId] = useState(readLocalSkin);
  const [tutorialComplete, setTutorialComplete] = useState(readTutorialComplete);
  const [botCount, setBotCount] = useState(DEFAULT_YAK_TRAILS_CONFIG.world.defaultBotFillTarget);
  const [teamFillTarget, setTeamFillTarget] = useState(DEFAULT_YAK_TRAILS_CONFIG.world.defaultBotFillTarget);
  const [teamLives, setTeamLives] = useState<number | null>(DEFAULT_YAK_TRAILS_CONFIG.teams.defaultLivesPerPlayer);
  const [partyMode, setPartyMode] = useState<GameMode>("ffa");
  const [partyTeamLives, setPartyTeamLives] = useState<number | null>(DEFAULT_YAK_TRAILS_CONFIG.teams.defaultLivesPerPlayer);
  const [party, setParty] = useState<PartySummary | null>(null);
  const [tournaments, setTournaments] = useState<TournamentSummary[]>([]);
  const [selectedTournamentId, setSelectedTournamentId] = useState(initialTournamentId ?? "");
  const [tournamentStandings, setTournamentStandings] = useState<PublicTournamentStanding[]>([]);
  const [tournamentBracket, setTournamentBracket] = useState<PublicTournamentBracket>();
  const [checkedInTournamentIds, setCheckedInTournamentIds] = useState<Set<string>>(new Set());
  const [tournamentNotice, setTournamentNotice] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<import("@games-monorepo/yak-trails-core").GameSnapshot | null>(null);
  const [territory, setTerritory] = useState<TerritoryState>(EMPTY_TERRITORY);
  const [playerId, setPlayerId] = useState("");
  const [result, setResult] = useState<RunResult | null>(null);
  const [unlockReveal, setUnlockReveal] = useState<SkinDefinition | null>(null);
  const [sessionKind, setSessionKind] = useState<SessionKind>("practice");
  const [networkQuality, setNetworkQuality] = useState<"good" | "degraded" | "offline">("good");
  const [networkLatencyMs, setNetworkLatencyMs] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackPreferences, setFeedbackPreferences] = useState<GameFeedbackPreferences>({
    soundEnabled: true,
    hapticsEnabled: true,
  });
  const [spectatorTargetId, setSpectatorTargetId] = useState<string>();
  const [quality, setQuality] = useState<"high" | "low">(initialRenderQuality);
  const [captureBurstTick, setCaptureBurstTick] = useState(0);
  const [reconnectExpiresAt, setReconnectExpiresAt] = useState(0);
  const [reconnectSeconds, setReconnectSeconds] = useState(0);
  const [reconnectFailed, setReconnectFailed] = useState(false);
  const transportRef = useRef<RoomTransport | null>(null);
  const playerIdRef = useRef("");
  const sessionKindRef = useRef<SessionKind>("practice");
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const territoryRef = useRef(territory);
  const bootstrapRef = useRef(bootstrap);
  const joinedInitialParty = useRef(false);
  const joinedInitialTournament = useRef(false);
  const joinedInitialAdmin = useRef(false);
  const seenResults = useRef(new Set<string>());
  const frameSamples = useRef<number[]>([]);
  const startInFlightRef = useRef(false);
  const connectionAttemptRef = useRef(0);
  const lastQuickScopeRef = useRef<"school" | "everyone">("everyone");
  const lastTeamScopeRef = useRef<"school" | "everyone">("everyone");
  const lastTeamWasLocalRef = useRef(false);
  const lastModeRef = useRef<"ffa" | "team">("ffa");
  const tournamentRespawnPolicyRef = useRef<RoomConnection["tournamentRespawnPolicy"]>(undefined);
  const requestedTournamentRespawnLifeRef = useRef<{ life: number; tick: number } | undefined>(undefined);
  const feedbackRef = useRef<GameFeedbackController | null>(null);
  const gameRootRef = useRef<HTMLDivElement>(null);
  useGameViewport(gameRootRef);

  const ensureFeedback = useCallback(() => {
    feedbackRef.current ??= new GameFeedbackController();
    return feedbackRef.current;
  }, []);

  useEffect(() => {
    const feedback = ensureFeedback();
    return feedback.subscribe((preferences) => setFeedbackPreferences({ ...preferences }));
  }, [ensureFeedback]);

  useEffect(() => () => {
    const feedback = feedbackRef.current;
    feedbackRef.current = null;
    void feedback?.dispose();
  }, []);

  const track = useCallback((event: string, context: Record<string, unknown> = {}) => {
    const versionContext = {
      protocolVersion: YAK_TRAILS_PROTOCOL_VERSION,
      rulesetVersion: DEFAULT_YAK_TRAILS_CONFIG.rulesetVersion,
      configVersion: DEFAULT_YAK_TRAILS_CONFIG.configVersion,
      ...context,
    };
    onTelemetry?.(event, versionContext);
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("yak-trails:telemetry", { detail: { event, context: versionContext } }));
    }
  }, [onTelemetry]);

  useEffect(() => {
    territoryRef.current = territory;
  }, [territory]);

  useEffect(() => {
    bootstrapRef.current = bootstrap;
  }, [bootstrap]);

  useLayoutEffect(() => {
    if (screen !== "playing") return;
    let animationFrame = 0;
    const resetPlayViewport = () => {
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      window.scrollTo(0, 0);
      gameRootRef.current?.scrollTo(0, 0);
    };
    resetPlayViewport();
    animationFrame = window.requestAnimationFrame(resetPlayViewport);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [screen]);

  useEffect(() => {
    if (!onlineEnabled && !suppliedSessionApi && !initialPartySlug && !initialTournamentId) {
      setConnectionStatus("idle");
      return;
    }
    let active = true;
    setConnectionStatus("bootstrapping");
    void (async () => {
      for (const retryDelay of YAK_TRAILS_CLIENT_TUNING.startup.bootstrapRetryDelaysMs) {
        if (retryDelay > 0) await new Promise((resolve) => window.setTimeout(resolve, retryDelay));
        if (!active) return;
        try {
          const resultValue = await sessionApi.bootstrap();
          if (!active) return;
          const skins = resultValue.skins.length > 0 ? resultValue.skins : YAK_TRAILS_SKINS;
          setBootstrap({ ...resultValue, skins });
          if (resultValue.profile) setSelectedSkinId(resultValue.profile.selectedSkinId);
          setConnectionStatus("idle");
          return;
        } catch {
          // Short-lived mobile network transitions should not immediately disable hosted play.
        }
      }
      if (!active) return;
      setBootstrap({ skins: YAK_TRAILS_SKINS, onlineAvailable: false, schoolAvailable: false });
      setConnectionStatus("idle");
      setMessage("Practice is ready. Sign in to Sidechat for online play.");
    })();
    return () => {
      active = false;
    };
  }, [initialAdminScreen, initialPartySlug, initialTournamentId, onlineEnabled, sessionApi, suppliedSessionApi]);

  useEffect(() => {
    if (!reconnectExpiresAt) return;
    const update = () => setReconnectSeconds(Math.max(0, Math.ceil((reconnectExpiresAt - Date.now()) / 1_000)));
    update();
    const timer = window.setInterval(update, YAK_TRAILS_CLIENT_TUNING.startup.reconnectCountdownIntervalMs);
    return () => window.clearInterval(timer);
  }, [reconnectExpiresAt]);

  useEffect(() => {
    if (screen !== "playing") return;
    let previous = performance.now();
    let animationFrame = 0;
    const sample = (now: number) => {
      const delta = now - previous;
      previous = now;
      frameSamples.current.push(delta);
      if (frameSamples.current.length > YAK_TRAILS_CLIENT_TUNING.quality.sampleCount) frameSamples.current.shift();
      if (frameSamples.current.length === YAK_TRAILS_CLIENT_TUNING.quality.sampleCount) {
        const average = frameSamples.current.reduce((sum, value) => sum + value, 0) / frameSamples.current.length;
        if (average > YAK_TRAILS_CLIENT_TUNING.quality.downgradeAverageFrameMs && quality === "high") {
          frameSamples.current = [];
          setQuality("low");
          track("quality_tier_changed", { quality: "low", averageFrameMs: Math.round(average * 10) / 10 });
        } else if (
          average < YAK_TRAILS_CLIENT_TUNING.quality.upgradeAverageFrameMs
          && quality === "low"
          && window.innerWidth > YAK_TRAILS_CLIENT_TUNING.quality.mobileWidthBreakpoint
        ) {
          frameSamples.current = [];
          setQuality("high");
          track("quality_tier_changed", { quality: "high", averageFrameMs: Math.round(average * 10) / 10 });
        }
      }
      animationFrame = requestAnimationFrame(sample);
    };
    animationFrame = requestAnimationFrame(sample);
    return () => cancelAnimationFrame(animationFrame);
  }, [quality, screen, track]);

  const handleResult = useCallback((nextResult: RunResult) => {
    if (seenResults.current.has(nextResult.id)) return;
    seenResults.current.add(nextResult.id);
    setResult(nextResult);
    if (nextResult.sessionKind === "practice") savePracticeBest(nextResult);
    if (nextResult.progressionEligible) {
      const previousUnlocks = new Set(bootstrapRef.current.profile?.unlockedSkinIds ?? []);
      void (async () => {
        for (const delay of YAK_TRAILS_CLIENT_TUNING.startup.profileRefreshDelaysMs) {
          await new Promise((resolve) => window.setTimeout(resolve, delay));
          try {
            const refreshed = await sessionApi.bootstrap();
            setBootstrap(refreshed);
            const newlyUnlocked = refreshed.skins
              .filter((skin) => refreshed.profile?.unlockedSkinIds.includes(skin.id) && !previousUnlocks.has(skin.id))
              .sort((left, right) => left.unlockXp - right.unlockXp)[0];
            if (newlyUnlocked) {
              setUnlockReveal(newlyUnlocked);
              ensureFeedback().emit("unlock", `${nextResult.id}:${newlyUnlocked.id}`);
              track("skin_unlocked", { skinId: newlyUnlocked.id, unlockXp: newlyUnlocked.unlockXp });
              break;
            }
            if (refreshed.profile && refreshed.profile.xp >= (bootstrapRef.current.profile?.xp ?? 0) + nextResult.xpAwarded) break;
          } catch {
            // Result presentation remains authoritative even if profile refresh is delayed.
          }
        }
      })();
    }
    track("run_completed", {
      sessionKind: nextResult.sessionKind,
      resultId: nextResult.id,
      placement: nextResult.placement,
      peakTerritoryPercent: nextResult.peakTerritoryPercent,
      eliminations: nextResult.eliminations,
      xpAwarded: nextResult.xpAwarded,
    });
  }, [ensureFeedback, sessionApi, track]);

  const handleRoomEvent = useCallback((event: RoomEvent) => {
    if (event.type === "connected") {
      setPlayerId(event.playerId);
      playerIdRef.current = event.playerId;
      setConnectionStatus("idle");
      setReconnectExpiresAt(0);
      setReconnectFailed(false);
      if (event.reconnected) {
        ensureFeedback().emit("reconnect_success", `connection:${connectionAttemptRef.current}`);
      }
      track(event.reconnected ? "reconnect_succeeded" : "room_joined", { sessionKind: sessionKindRef.current });
    } else if (event.type === "snapshot") {
      setSnapshot(event.snapshot);
      if (event.territory) {
        setTerritory((current) => {
          if (!event.territory!.full && event.territory!.baseRevision !== current.revision) {
            transportRef.current?.requestResync();
            return current;
          }
          return applyTerritoryPatch(current, event.territory!, GRID_SIZE);
        });
      }
      setScreen("playing");
    } else if (event.type === "territory") {
      setTerritory((current) => {
        if (!event.patch.full && event.patch.baseRevision !== current.revision) {
          transportRef.current?.requestResync();
          return current;
        }
        return applyTerritoryPatch(current, event.patch, GRID_SIZE);
      });
    } else if (event.type === "network") {
      setNetworkQuality(event.quality);
      if (event.latencyMs !== undefined) setNetworkLatencyMs(event.latencyMs);
      if (event.quality === "good") {
        setConnectionStatus("idle");
        setReconnectExpiresAt(0);
      }
    } else if (event.type === "reconnecting") {
      setConnectionStatus("reconnecting");
      setReconnectExpiresAt(event.expiresAtMs);
      track("reconnect_attempted", { attempt: event.attempt, sessionKind: sessionKindRef.current });
    } else if (event.type === "error") {
      setMessage(event.message);
      if (event.code === "reconnect_expired") {
        setConnectionStatus("idle");
        setReconnectExpiresAt(0);
        setReconnectFailed(true);
        setResult(null);
        setMenuOpen(false);
        ensureFeedback().emit("reconnect_failure", `connection:${connectionAttemptRef.current}:${event.code}`);
        track("reconnect_failed", { code: event.code, sessionKind: sessionKindRef.current });
      }
      else if (!event.retryable && event.code === "protocol_error") {
        track("protocol_mismatch", { code: event.code });
      } else {
        track("network_failure", { code: event.code, sessionKind: sessionKindRef.current });
      }
    }
    if (event.type === "events") {
      for (const simulationEvent of event.events) {
        const eventKey = `connection:${connectionAttemptRef.current}:tick:${simulationEvent.tick}`;
        if (simulationEvent.type === "territory_captured" && simulationEvent.playerId === playerIdRef.current) {
          setCaptureBurstTick(simulationEvent.tick);
          ensureFeedback().emit("capture", `${eventKey}:${simulationEvent.playerId}`);
        } else if (simulationEvent.type === "player_eliminated") {
          if (simulationEvent.playerId === playerIdRef.current) {
            ensureFeedback().emit("death", `${eventKey}:${simulationEvent.playerId}`);
          } else if (simulationEvent.byPlayerId === playerIdRef.current) {
            ensureFeedback().emit("cut", `${eventKey}:${simulationEvent.playerId}`);
          }
        } else if (simulationEvent.type === "player_respawned" && simulationEvent.playerId === playerIdRef.current) {
          ensureFeedback().emit("respawn", `${eventKey}:${simulationEvent.playerId}`);
        } else if (simulationEvent.type === "team_victory") {
          const selfResult = simulationEvent.results.find((candidate) => candidate.playerId === playerIdRef.current);
          if (selfResult?.teamId) {
            ensureFeedback().emit(
              selfResult.teamId === simulationEvent.winnerTeamId ? "team_win" : "team_loss",
              `${eventKey}:${simulationEvent.winnerTeamId}`,
            );
          }
        }
      }
    }
    const eventResult = resultFromRoomEvent(event, playerIdRef.current);
    const isIntermediateTournamentLife = event.type === "events"
      && sessionKindRef.current === "tournament"
      && tournamentRespawnPolicyRef.current !== undefined
      && tournamentRespawnPolicyRef.current !== "elimination";
    if (eventResult && !isIntermediateTournamentLife) handleResult(eventResult);
  }, [ensureFeedback, handleResult, track]);

  const withStartLock = useCallback(async (operation: () => Promise<void>) => {
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;
    setIsStarting(true);
    try {
      await operation();
    } finally {
      startInFlightRef.current = false;
      setIsStarting(false);
    }
  }, []);

  const connectToRoom = useCallback(async (
    transport: RoomTransport,
    connection: RoomConnection,
    kind: SessionKind,
  ): Promise<boolean> => {
    if (
      connection.protocolVersion !== YAK_TRAILS_PROTOCOL_VERSION ||
      connection.rulesetVersion !== DEFAULT_YAK_TRAILS_CONFIG.rulesetVersion ||
      connection.configVersion !== DEFAULT_YAK_TRAILS_CONFIG.configVersion ||
      connection.configHash !== DEFAULT_YAK_TRAILS_CONFIG_HASH
    ) {
      track("protocol_mismatch", {
        roomProtocolVersion: connection.protocolVersion,
        roomRulesetVersion: connection.rulesetVersion,
        roomConfigVersion: connection.configVersion,
      });
      throw new Error("This arena uses an incompatible game version. Please refresh Yak Trails.");
    }
    const attempt = connectionAttemptRef.current + 1;
    connectionAttemptRef.current = attempt;
    const previousTransport = transportRef.current;
    transportRef.current = null;
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    await previousTransport?.disconnect("leave");
    if (attempt !== connectionAttemptRef.current) {
      await transport.disconnect("leave");
      return false;
    }
    setSnapshot(null);
    setTerritory(EMPTY_TERRITORY);
    setResult(null);
    setUnlockReveal(null);
    setCaptureBurstTick(0);
    setReconnectFailed(false);
    setSessionKind(kind);
    sessionKindRef.current = kind;
    tournamentRespawnPolicyRef.current = connection.tournamentRespawnPolicy;
    requestedTournamentRespawnLifeRef.current = undefined;
    setScreen("connecting");
    setConnectionStatus("matching");
    setMessage(null);
    setTournamentNotice(null);
    transportRef.current = transport;
    unsubscribeRef.current = transport.subscribe(handleRoomEvent);
    try {
      await transport.connect(connection);
      if (attempt !== connectionAttemptRef.current) {
        await transport.disconnect("leave");
        return false;
      }
      return true;
    } catch (error) {
      await transport.disconnect("leave");
      if (attempt !== connectionAttemptRef.current) return false;
      if (transportRef.current === transport) transportRef.current = null;
      unsubscribeRef.current?.();
      unsubscribeRef.current = null;
      setScreen("home");
      setConnectionStatus("idle");
      throw error;
    }
  }, [handleRoomEvent, track]);

  const startPractice = useCallback(async (resume: boolean) => {
    await withStartLock(async () => {
      lastModeRef.current = "ffa";
      lastTeamWasLocalRef.current = false;
      track("practice_started", { botCount, skinId: selectedSkinId, resume });
      const transport = new LocalPracticeTransport({ botCount, skinId: selectedSkinId, resume, ...(practiceWorkerUrl ? { workerUrl: practiceWorkerUrl } : {}) });
      try {
        await connectToRoom(transport, LocalPracticeTransport.connection(), "practice");
      } catch (error) {
        setScreen("practice");
        setMessage(error instanceof Error ? error.message : "Practice could not start.");
      }
    });
  }, [botCount, connectToRoom, practiceWorkerUrl, selectedSkinId, track, withStartLock]);

  const startLocalTeamBattle = useCallback(async () => {
    await withStartLock(async () => {
      lastTeamWasLocalRef.current = true;
      lastModeRef.current = "team";
      track("team_battle_requested", {
        audience: "bots",
        botFillTarget: teamFillTarget,
        livesPerPlayer: teamLives,
      });
      const transport = new LocalPracticeTransport({
        botCount: Math.max(1, teamFillTarget - 1),
        skinId: selectedSkinId,
        resume: false,
        mode: "team",
        teamRules: {
          livesPerPlayer: teamLives,
          territoryVictoryPercent: DEFAULT_YAK_TRAILS_CONFIG.teams.territoryVictoryPercent,
        },
        ...(practiceWorkerUrl ? { workerUrl: practiceWorkerUrl } : {}),
      });
      try {
        await connectToRoom(transport, LocalPracticeTransport.connection(), "practice");
      } catch (error) {
        setScreen("team");
        setMessage(error instanceof Error ? error.message : "Team battle could not start.");
      }
    });
  }, [connectToRoom, practiceWorkerUrl, selectedSkinId, teamFillTarget, teamLives, track, withStartLock]);

  const startHosted = useCallback(async (kind: "ranked_solo" | "quick", scope?: "school" | "everyone") => {
    await withStartLock(async () => {
      lastModeRef.current = "ffa";
      if (kind === "quick" && scope) lastQuickScopeRef.current = scope;
      const requestGeneration = connectionAttemptRef.current;
      setScreen("connecting");
      setConnectionStatus("matching");
      setMessage(null);
      track(kind === "quick" ? "matchmaking_requested" : "menu_mode_selected", { sessionKind: kind, scope });
      try {
        const connection = kind === "ranked_solo"
          ? await sessionApi.startRankedSolo({ skinId: selectedSkinId })
          : await sessionApi.joinQuickMatch({ skinId: selectedSkinId, scope: scope! });
        if (requestGeneration !== connectionAttemptRef.current) return;
        track(kind === "quick" ? "matchmaking_matched" : "room_reserved", { sessionKind: kind, scope });
        await connectToRoom(new WebSocketRoomTransport(), connection, kind);
      } catch (error) {
        setConnectionStatus("idle");
        setScreen(kind === "quick" ? "online" : "home");
        setMessage(error instanceof Error ? error.message : "The arena is unavailable.");
        track(kind === "quick" ? "matchmaking_failed" : "network_failure", { sessionKind: kind, scope });
      }
    });
  }, [connectToRoom, selectedSkinId, sessionApi, track, withStartLock]);

  const startHostedTeamBattle = useCallback(async (scope: "school" | "everyone") => {
    await withStartLock(async () => {
      lastTeamScopeRef.current = scope;
      lastTeamWasLocalRef.current = false;
      lastModeRef.current = "team";
      const requestGeneration = connectionAttemptRef.current;
      setScreen("connecting");
      setConnectionStatus("matching");
      setMessage(null);
      track("team_battle_requested", {
        audience: scope,
        botFillTarget: teamFillTarget,
        livesPerPlayer: teamLives,
      });
      try {
        const connection = await sessionApi.startTeamBattle({
          skinId: selectedSkinId,
          scope,
          botFillTarget: teamFillTarget,
          livesPerPlayer: teamLives,
        });
        if (requestGeneration !== connectionAttemptRef.current) return;
        track("team_battle_matched", { audience: scope, botFillTarget: teamFillTarget });
        await connectToRoom(new WebSocketRoomTransport(), connection, "quick");
      } catch (error) {
        setConnectionStatus("idle");
        setScreen("team");
        setMessage(error instanceof Error ? error.message : "Team matchmaking is unavailable.");
        track("matchmaking_failed", { sessionKind: "quick", mode: "team", scope });
      }
    });
  }, [connectToRoom, selectedSkinId, sessionApi, teamFillTarget, teamLives, track, withStartLock]);

  const joinPartyArena = useCallback(async (slug: string) => {
    await withStartLock(async () => {
      const requestGeneration = connectionAttemptRef.current;
      setScreen("connecting");
      setConnectionStatus("matching");
      setMessage(null);
      try {
        const summary = await sessionApi.joinParty(slug);
        if (requestGeneration !== connectionAttemptRef.current) return;
        setParty(summary);
        setBotCount(summary.botFillTarget);
        setPartyMode(summary.mode);
        if (summary.teamRules) setPartyTeamLives(summary.teamRules.livesPerPlayer);
        lastModeRef.current = summary.mode;
        lastTeamWasLocalRef.current = false;
        track("party_joined", { slug });
        const connection = await sessionApi.resolvePartyRoom(slug);
        if (requestGeneration !== connectionAttemptRef.current) return;
        await connectToRoom(new WebSocketRoomTransport(), connection, "party");
      } catch (error) {
        setScreen("party");
        setConnectionStatus("idle");
        setMessage(error instanceof Error ? error.message : "That party is unavailable.");
      }
    });
  }, [connectToRoom, sessionApi, track, withStartLock]);

  const loadPartyInvite = useCallback(async (slug: string) => {
    await withStartLock(async () => {
      setScreen("connecting");
      setConnectionStatus("matching");
      setMessage(null);
      try {
        const summary = await sessionApi.joinParty(slug);
        setParty(summary);
        setBotCount(summary.botFillTarget);
        setPartyMode(summary.mode);
        if (summary.teamRules) setPartyTeamLives(summary.teamRules.livesPerPlayer);
        setScreen("party");
        setConnectionStatus("idle");
        track("party_joined", { slug, mode: summary.mode, round: summary.round, invitePreview: true });
      } catch (error) {
        setScreen("party");
        setConnectionStatus("idle");
        setMessage(error instanceof Error ? error.message : "That party is unavailable.");
      }
    });
  }, [sessionApi, track, withStartLock]);

  const loadTournamentHub = useCallback(async (preferredTournamentId?: string) => {
    setScreen("tournaments");
    setConnectionStatus("matching");
    setMessage(null);
    setTournamentNotice(null);
    try {
      const available = await sessionApi.listTournaments();
      setTournaments(available);
      const requested = preferredTournamentId
        ? available.find((tournament) => tournament.id === preferredTournamentId)
        : undefined;
      const nextId = requested?.id ?? (selectedTournamentId || available[0]?.id || "");
      setSelectedTournamentId(nextId);
      if (nextId) {
        window.history.replaceState(null, "", `#/yak-trails/tournament/${encodeURIComponent(nextId)}`);
        const [standings, bracket] = await Promise.all([
          sessionApi.getTournamentStandings(nextId),
          sessionApi.getTournamentBracket(nextId),
        ]);
        setTournamentStandings(standings);
        setTournamentBracket(bracket);
        if (bracket.checkedIn) setCheckedInTournamentIds((current) => new Set(current).add(nextId));
      } else {
        setTournamentStandings([]);
        setTournamentBracket(undefined);
      }
      setConnectionStatus("idle");
      track("tournament_catalog_viewed", { tournamentCount: available.length });
    } catch (error) {
      setConnectionStatus("idle");
      setMessage(error instanceof Error ? error.message : "Tournaments could not be loaded.");
    }
  }, [selectedTournamentId, sessionApi, track]);

  const selectTournament = useCallback(async (tournamentId: string) => {
    setSelectedTournamentId(tournamentId);
    setMessage(null);
    setTournamentNotice(null);
    window.history.replaceState(null, "", `#/yak-trails/tournament/${encodeURIComponent(tournamentId)}`);
    try {
      const [standings, bracket] = await Promise.all([
        sessionApi.getTournamentStandings(tournamentId),
        sessionApi.getTournamentBracket(tournamentId),
      ]);
      setTournamentStandings(standings);
      setTournamentBracket(bracket);
      if (bracket.checkedIn) setCheckedInTournamentIds((current) => new Set(current).add(tournamentId));
      track("tournament_viewed", { tournamentId });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Standings could not be loaded.");
    }
  }, [sessionApi, track]);

  const enterTournament = useCallback(async (tournament: TournamentSummary) => {
    await withStartLock(async () => {
      setMessage(null);
      setTournamentNotice(null);
      try {
        await sessionApi.checkInTournament(tournament.id);
        setCheckedInTournamentIds((current) => new Set(current).add(tournament.id));
        track("tournament_checked_in", { tournamentId: tournament.id, status: tournament.status });
        setTournamentBracket(await sessionApi.getTournamentBracket(tournament.id));
        if (tournament.status !== "active") {
          setTournamentNotice("You’re checked in. Come back when the first heat goes live.");
          return;
        }
        const resolution = await sessionApi.resolveTournamentRoom(tournament.id);
        if (resolution.kind === "bye") {
          setTournamentNotice(resolution.message);
          setTournamentStandings(await sessionApi.getTournamentStandings(tournament.id));
          setTournamentBracket(await sessionApi.getTournamentBracket(tournament.id));
          track("tournament_bye_awarded", {
            tournamentId: tournament.id,
            stageId: resolution.stageId,
            nextStageId: resolution.nextStageId,
          });
          return;
        }
        lastModeRef.current = tournament.mode;
        track("tournament_heat_joined", { tournamentId: tournament.id, mode: tournament.mode });
        await connectToRoom(new WebSocketRoomTransport(), resolution.connection, "tournament");
      } catch (error) {
        setScreen("tournaments");
        setConnectionStatus("idle");
        setMessage(error instanceof Error ? error.message : "Your tournament heat is unavailable.");
        track("tournament_join_failed", { tournamentId: tournament.id });
      }
    });
  }, [connectToRoom, sessionApi, track, withStartLock]);

  useEffect(() => {
    if (!initialPartySlug || joinedInitialParty.current || connectionStatus === "bootstrapping") return;
    joinedInitialParty.current = true;
    void loadPartyInvite(initialPartySlug);
  }, [connectionStatus, initialPartySlug, loadPartyInvite]);

  useEffect(() => {
    if (!initialTournamentId || joinedInitialTournament.current || connectionStatus === "bootstrapping") return;
    joinedInitialTournament.current = true;
    void loadTournamentHub(initialTournamentId);
  }, [connectionStatus, initialTournamentId, loadTournamentHub]);

  useEffect(() => {
    if (!initialAdminScreen || joinedInitialAdmin.current || connectionStatus === "bootstrapping") return;
    joinedInitialAdmin.current = true;
    if (bootstrap.tournamentAdminAvailable) {
      setScreen("tournament-admin");
      setConnectionStatus("idle");
      track("tournament_admin_opened", { directRoute: true });
    } else {
      window.history.replaceState(null, "", "#/yak-trails");
      setScreen("home");
      setConnectionStatus("idle");
      setMessage("Tournament operations require a staff account.");
    }
  }, [bootstrap.tournamentAdminAvailable, connectionStatus, initialAdminScreen, track]);

  useEffect(() => () => {
    connectionAttemptRef.current += 1;
    unsubscribeRef.current?.();
    void transportRef.current?.disconnect("shutdown");
  }, []);

  useEffect(() => {
    const pauseLocalPractice = () => {
      if (sessionKind !== "practice" || screen !== "playing") return;
      const transport = transportRef.current;
      if (transport instanceof LocalPracticeTransport) transport.pause();
      setMenuOpen(true);
    };
    const refreshHostedConnection = () => {
      if (sessionKind === "practice" || screen !== "playing") return;
      const transport = transportRef.current;
      if (transport instanceof WebSocketRoomTransport) transport.refreshAfterForeground();
    };
    const onVisibility = () => {
      if (document.hidden) pauseLocalPractice();
      else refreshHostedConnection();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("lifecycle-pause", pauseLocalPractice);
    window.addEventListener("lifecycle-resume", refreshHostedConnection);
    window.addEventListener("pageshow", refreshHostedConnection);
    window.addEventListener("focus", refreshHostedConnection);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("lifecycle-pause", pauseLocalPractice);
      window.removeEventListener("lifecycle-resume", refreshHostedConnection);
      window.removeEventListener("pageshow", refreshHostedConnection);
      window.removeEventListener("focus", refreshHostedConnection);
    };
  }, [screen, sessionKind]);

  useEffect(() => {
    if (!snapshot) {
      setSpectatorTargetId(undefined);
      return;
    }
    const localPlayerId = snapshot.selfPlayerId ?? playerIdRef.current;
    const localPlayer = snapshot.players.find((player) => player.id === localPlayerId);
    const shouldFollow = localPlayer?.status !== "active"
      && !result
      && snapshot.teamBattle?.status !== "finished";
    if (!localPlayer || !shouldFollow) {
      setSpectatorTargetId(undefined);
      return;
    }
    setSpectatorTargetId((current) => resolveSpectatorTargetId(snapshot, localPlayerId, current));
  }, [result, snapshot]);

  useEffect(() => {
    if (
      sessionKind !== "tournament"
      || tournamentRespawnPolicyRef.current === undefined
      || tournamentRespawnPolicyRef.current === "elimination"
      || !snapshot
      || result
    ) return;
    const localPlayerId = snapshot.selfPlayerId ?? playerIdRef.current;
    const localPlayer = snapshot.players.find((player) => player.id === localPlayerId);
    if (!localPlayer || localPlayer.status === "active") {
      requestedTournamentRespawnLifeRef.current = undefined;
      return;
    }
    const life = localPlayer.livesStarted ?? 1;
    if (
      localPlayer.canRespawn
      && (localPlayer.respawnAtMs ?? 0) <= snapshot.serverTimeMs
      && (
        requestedTournamentRespawnLifeRef.current?.life !== life
        || snapshot.tick - requestedTournamentRespawnLifeRef.current.tick >= DEFAULT_YAK_TRAILS_CONFIG.mechanics.simulationHz
      )
    ) {
      requestedTournamentRespawnLifeRef.current = { life, tick: snapshot.tick };
      transportRef.current?.requestRespawn();
      track("tournament_respawn_requested", {
        life,
        policy: tournamentRespawnPolicyRef.current,
      });
    }
  }, [result, sessionKind, snapshot, track]);

  const selfId = snapshot?.selfPlayerId ?? playerId;
  const selfPlayer = snapshot?.players.find((player) => player.id === selfId);
  const teamBattle = snapshot?.teamBattle;
  const teamFinished = teamBattle?.status === "finished";
  const teamPreStart = teamBattle?.status === "waiting" || teamBattle?.status === "countdown";
  const teamCountdownSeconds = teamBattle?.status === "countdown" && teamBattle.countdownEndsAtMs
    ? Math.max(1, Math.ceil((teamBattle.countdownEndsAtMs - snapshot!.serverTimeMs) / 1_000))
    : null;
  const tournamentSecondsRemaining = sessionKind === "tournament" && snapshot?.sessionEndsAtMs
    ? Math.max(0, Math.ceil((snapshot.sessionEndsAtMs - snapshot.serverTimeMs) / 1_000))
    : null;
  const selfTeamId = selfPlayer?.teamId;
  const teamWon = Boolean(teamFinished && selfTeamId && teamBattle.winnerTeamId === selfTeamId);
  const isSpectating = selfPlayer !== undefined && selfPlayer.status !== "active" && !result && !teamFinished;
  const spectatorActions = selfPlayer && isSpectating && snapshot
    ? spectatorActionState(
        selfPlayer,
        sessionKind,
        snapshot.serverTimeMs,
        snapshot.tournamentRespawnPolicy,
      )
    : null;
  const spectatorTarget = snapshot?.players.find((player) => player.id === spectatorTargetId);
  const steeringControls = useSteeringControls(
    transportRef.current,
    screen === "playing" && !menuOpen && !result && !isSpectating && !teamPreStart,
    snapshot?.tick ?? 0,
  );
  const { directionRef: steeringDirection, ...controls } = steeringControls;
  const onlineAvailable = onlineEnabled && bootstrap.onlineAvailable;
  const muted = !feedbackPreferences.soundEnabled;
  const skins = bootstrap.skins.length > 0 ? bootstrap.skins : YAK_TRAILS_SKINS;
  const unlocked = new Set(bootstrap.profile?.unlockedSkinIds ?? YAK_TRAILS_SKINS.filter((skin) => skin.starter).map((skin) => skin.id));
  const selectedTournament = tournaments.find((tournament) => tournament.id === selectedTournamentId)
    ?? tournaments[0];
  const selectedTournamentStage = tournamentBracket?.stages.find((stage) => (
    stage.startsAtMs <= Date.now() && stage.endsAtMs >= Date.now()
  )) ?? tournamentBracket?.stages[0];

  const showHome = () => {
    if (window.location.hash.startsWith("#/yak-trails/")) {
      window.history.replaceState(null, "", "#/yak-trails");
    }
    setScreen("home");
    setMessage(null);
  };

  const cancelConnection = async () => {
    connectionAttemptRef.current += 1;
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    const transport = transportRef.current;
    transportRef.current = null;
    await transport?.disconnect("leave");
    playerIdRef.current = "";
    setPlayerId("");
    setSnapshot(null);
    setTerritory(EMPTY_TERRITORY);
    setConnectionStatus("idle");
    setReconnectFailed(false);
    setIsStarting(false);
    showHome();
  };

  const leaveArena = async () => {
    connectionAttemptRef.current += 1;
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    await transportRef.current?.disconnect("leave");
    transportRef.current = null;
    playerIdRef.current = "";
    setSnapshot(null);
    setTerritory(EMPTY_TERRITORY);
    setResult(null);
    setUnlockReveal(null);
    setMenuOpen(false);
    setConnectionStatus("idle");
    setReconnectFailed(false);
    if (sessionKind === "practice") track("practice_ended", {});
    showHome();
  };

  const returnToTournament = async () => {
    const tournamentId = result?.tournamentId ?? selectedTournamentId;
    await leaveArena();
    if (!tournamentId) return;
    window.history.replaceState(null, "", `#/yak-trails/tournament/${encodeURIComponent(tournamentId)}`);
    await loadTournamentHub(tournamentId);
  };

  const closeMenu = () => {
    if (sessionKind === "practice" && transportRef.current instanceof LocalPracticeTransport) {
      transportRef.current.resume();
    }
    setMenuOpen(false);
  };

  const openMenu = () => {
    if (sessionKind === "practice" && transportRef.current instanceof LocalPracticeTransport) {
      transportRef.current.pause();
    }
    setMenuOpen(true);
  };

  const respawn = () => {
    if (sessionKind === "tournament") {
      void returnToTournament();
      return;
    }
    setResult(null);
    setUnlockReveal(null);
    setSpectatorTargetId(undefined);
    track("respawn", { sessionKind });
    if (sessionKind === "ranked_solo") {
      void startHosted("ranked_solo");
      return;
    }
    transportRef.current?.requestRespawn();
    window.setTimeout(
      () => transportRef.current?.requestRespawn(),
      DEFAULT_YAK_TRAILS_CONFIG.mechanics.respawnDelayMs
        + YAK_TRAILS_CLIENT_TUNING.startup.respawnRetryPaddingMs,
    );
  };

  const spectate = () => {
    setResult(null);
    setUnlockReveal(null);
    track("spectate", { sessionKind });
  };

  const replayTeamBattle = () => {
    if (sessionKind === "party" && party) {
      void withStartLock(async () => {
        try {
          let current = await sessionApi.joinParty(party.slug);
          setParty(current);
          if (current.mode === "team" && (current.status === "between_rounds" || current.status === "active")) {
            if (current.hostPlayerId !== bootstrapRef.current.profile?.userId) {
              setMessage("Waiting for the party host to start the next round.");
              return;
            }
            current = await sessionApi.commandParty(current.slug, { type: "start_next_round", round: current.round });
            setParty(current);
            track("party_round_started", { slug: current.slug, round: current.round, mode: current.mode });
          }
          if (current.status === "open" || current.status === "active") {
            const connection = await sessionApi.resolvePartyRoom(current.slug);
            lastModeRef.current = current.mode;
            lastTeamWasLocalRef.current = false;
            await connectToRoom(new WebSocketRoomTransport(), connection, "party");
          }
        } catch (error) {
          setMessage(error instanceof Error ? error.message : "The next party round is not ready yet.");
        }
      });
      return;
    }
    if (lastTeamWasLocalRef.current) void startLocalTeamBattle();
    else void startHostedTeamBattle(lastTeamScopeRef.current);
  };

  const recoverHostedArena = () => {
    setReconnectFailed(false);
    setMessage(null);
    if (sessionKind === "tournament") {
      void returnToTournament();
      return;
    }
    if (sessionKind === "party" && party) {
      void joinPartyArena(party.slug);
      return;
    }
    if (lastModeRef.current === "team") {
      void startHostedTeamBattle(lastTeamScopeRef.current);
      return;
    }
    if (sessionKind === "ranked_solo") {
      void startHosted("ranked_solo");
      return;
    }
    void startHosted("quick", lastQuickScopeRef.current);
  };

  const createParty = async () => {
    await withStartLock(async () => {
      setConnectionStatus("matching");
      try {
        const created = await sessionApi.createParty({
          botFillTarget: botCount,
          skinId: selectedSkinId,
          mode: partyMode,
          ...(partyMode === "team" ? {
            teamRules: {
              livesPerPlayer: partyTeamLives,
              territoryVictoryPercent: DEFAULT_YAK_TRAILS_CONFIG.teams.territoryVictoryPercent,
            },
          } : {}),
        });
        setParty(created);
        setBotCount(created.botFillTarget);
        setPartyMode(created.mode);
        if (created.teamRules) setPartyTeamLives(created.teamRules.livesPerPlayer);
        setMessage(null);
        track("party_created", {
          slug: created.slug,
          botFillTarget: created.botFillTarget,
          mode: created.mode,
          livesPerPlayer: created.teamRules?.livesPerPlayer,
        });
        const route = `#/yak-trails/party/${created.slug}`;
        window.history.replaceState(null, "", route);
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "The party could not be created.");
      } finally {
        setConnectionStatus("idle");
      }
    });
  };

  const copyPartyLink = async () => {
    if (!party) return;
    const url = `${window.location.origin}${window.location.pathname}#/yak-trails/party/${party.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setMessage("Invite link copied!");
      track("party_shared", { slug: party.slug });
    } catch {
      setMessage(url);
    }
  };

  const updatePartyBotFill = async () => {
    if (!party) return;
    setConnectionStatus("matching");
    try {
      const updated = await sessionApi.commandParty(party.slug, { type: "set_bot_fill", botFillTarget: botCount });
      setParty(updated);
      setMessage(`Arena now fills toward ${updated.botFillTarget} players.`);
      track("party_bot_fill_changed", { slug: party.slug, botFillTarget: updated.botFillTarget });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Party settings could not be saved.");
    } finally {
      setConnectionStatus("idle");
    }
  };

  const closeParty = async () => {
    if (!party) return;
    setConnectionStatus("matching");
    try {
      await sessionApi.commandParty(party.slug, { type: "close" });
      track("party_closed", { slug: party.slug });
      setParty(null);
      window.history.replaceState(null, "", "#/yak-trails");
      showHome();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "The party could not be closed.");
    } finally {
      setConnectionStatus("idle");
    }
  };

  const selectSkin = async (skin: SkinDefinition) => {
    track("skin_viewed", { skinId: skin.id, locked: !unlocked.has(skin.id) });
    if (!unlocked.has(skin.id)) return;
    setSelectedSkinId(skin.id);
    writeLocalSkin(skin.id);
    if (bootstrap.profile) {
      try {
        const profile = await sessionApi.selectSkin(skin.id);
        setBootstrap((current) => ({ ...current, profile }));
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "Skin selection was not saved.");
      }
    }
    track("skin_selected", { skinId: skin.id });
  };

  const home = (
    <div className="yak-trails__screen yak-trails__home">
      <div className="yak-trails__home-orb yak-trails__home-orb--one" />
      <div className="yak-trails__home-orb yak-trails__home-orb--two" />
      <header className="yak-trails__brand-row">
        {onBack ? <button type="button" className="yak-trails__icon-button" onClick={onBack} aria-label="Back to games">←</button> : <span />}
        <div className="yak-trails__xp-chip" aria-label={`${bootstrap.profile?.xp ?? 0} experience points`}>
          <span>✦</span> {bootstrap.profile?.xp ?? 0} XP
        </div>
      </header>
      <div className="yak-trails__hero">
        <div className="yak-trails__hero-preview"><SkinShowcase skinId={selectedSkinId} /></div>
        <p className="yak-trails__eyebrow">CLAIM THE ARENA</p>
        <h1><span>YAK</span> TRAILS</h1>
        <p>Loop it. Claim it. Keep your trail safe.</p>
      </div>
      <div className="yak-trails__home-actions">
        {practiceEnabled && (
          <MenuTile emoji="🌿" title="Practice" detail={`Offline • choose 0–${DEFAULT_YAK_TRAILS_CONFIG.world.capacity - 1} bots`} onClick={() => { setScreen("practice"); track("menu_mode_selected", { sessionKind: "practice" }); }} accent="mint" />
        )}
        <div className="yak-trails__tile-grid">
          <MenuTile emoji="🥇" title="Ranked Solo" detail={onlineAvailable ? `You vs ${DEFAULT_YAK_TRAILS_CONFIG.world.capacity - 1} bots` : "Sign in"} onClick={() => void startHosted("ranked_solo")} disabled={!onlineAvailable} accent="sun" />
          <MenuTile emoji="🏆" title="Tournaments" detail={bootstrap.tournamentsAvailable ? "Check in & advance" : "Coming soon"} onClick={() => void loadTournamentHub()} disabled={!onlineAvailable || !bootstrap.tournamentsAvailable} accent="violet" />
          <MenuTile emoji="◎" title="Online" detail="School or everyone" onClick={() => { setScreen("online"); track("menu_mode_selected", { sessionKind: "quick" }); }} disabled={!onlineAvailable} accent="berry" />
          <MenuTile emoji="⚔️" title="Team Battle" detail="Red vs Blue" onClick={() => { setScreen("team"); track("menu_mode_selected", { sessionKind: "quick", mode: "team" }); }} disabled={!practiceEnabled && !onlineAvailable} accent="sun" />
          <MenuTile emoji="✦" title="Party" detail="Invite friends" onClick={() => setScreen("party")} disabled={!onlineAvailable} accent="violet" />
          <MenuTile emoji="🎨" title="Skins" detail={`${skins.length} voxel pals`} onClick={() => setScreen("skins")} accent="mint" />
        </div>
      </div>
      {message && <button type="button" className="yak-trails__toast" onClick={() => setMessage(null)}>{message}</button>}
      <footer className="yak-trails__home-footer">
        <button type="button" onClick={() => {
          setScreen("tutorial");
          track("tutorial_opened", { firstRun: !tutorialComplete });
        }}>{tutorialComplete ? "Replay Trail School" : "New here? Learn in 30 seconds"}</button>
        <span>Drag anywhere to steer • release to cruise</span>
      </footer>
    </div>
  );

  const practiceScreen = (
    <div className="yak-trails__screen yak-trails__subscreen">
      <ScreenHeader title="Practice" onBack={showHome} />
      <div className="yak-trails__subscreen-intro yak-trails__practice-art">
        <span className="yak-trails__big-icon">🌿</span>
        <h3>Make the arena yours</h3>
        <p>Offline, unranked, and deterministic. Pause any time and keep a local checkpoint.</p>
      </div>
      <div className="yak-trails__party-card yak-trails__practice-card">
        <label htmlFor="yak-trails-practice-bots">Bot opponents <strong>{botCount}</strong></label>
        <input
          id="yak-trails-practice-bots"
          type="range"
          min="0"
          max={DEFAULT_YAK_TRAILS_CONFIG.world.capacity - 1}
          value={botCount}
          onChange={(event) => setBotCount(Number(event.target.value))}
        />
        <div className="yak-trails__range-labels" aria-hidden="true"><span>Solo</span><span>Full arena</span></div>
        <small>Bot choices and simulation are seeded so the same checkpoint resumes exactly.</small>
        <Button disabled={isStarting} onClick={() => void startPractice(false)}>{isStarting ? "Starting…" : "Start fresh"}</Button>
        <Button disabled={isStarting} variant="secondary" onClick={() => void startPractice(true)}>Resume checkpoint</Button>
        <button type="button" className="yak-trails__practice-tutorial-link" onClick={() => setScreen("tutorial")}>How to play</button>
      </div>
      {message && <p className="yak-trails__inline-error">{message}</p>}
    </div>
  );

  const online = (
    <div className="yak-trails__screen yak-trails__subscreen">
      <ScreenHeader title="Online FFA" onBack={showHome} />
      <div className="yak-trails__subscreen-intro">
        <span className="yak-trails__big-icon">◎</span>
        <h3>Choose your crowd</h3>
        <p>Drop into a living arena. Bots make room as more people join.</p>
      </div>
      <div className="yak-trails__choice-stack">
        <MenuTile emoji="🎓" title="My School" detail={bootstrap.schoolAvailable ? "Verified school players only" : "No verified school found"} onClick={() => void startHosted("quick", "school")} disabled={!bootstrap.schoolAvailable} accent="mint" />
        <MenuTile emoji="🌎" title="Everyone" detail="Match with players everywhere" onClick={() => void startHosted("quick", "everyone")} accent="berry" />
      </div>
      <div className="yak-trails__privacy-note"><span>⌁</span> School rooms never widen into public matchmaking.</div>
      {message && <p className="yak-trails__inline-error">{message}</p>}
    </div>
  );

  const teamScreen = (
    <div className="yak-trails__screen yak-trails__subscreen yak-trails__team-screen">
      <ScreenHeader title="Team Battle" onBack={showHome} />
      <div className="yak-trails__subscreen-intro yak-trails__team-intro">
        <div className="yak-trails__team-emblem" aria-hidden="true"><span>R</span><b>VS</b><span>B</span></div>
        <h3>Red side. Blue side.</h3>
        <p>Cut enemy trails, protect your teammates, and take {DEFAULT_YAK_TRAILS_CONFIG.teams.territoryVictoryPercent}% of the arena—or wipe the other team out.</p>
      </div>
      <div className="yak-trails__party-card yak-trails__team-card">
        <label htmlFor="yak-trails-team-fill">
          Fill toward
          <strong>{teamFillTarget <= 1 ? "People only" : `${teamFillTarget} players`}</strong>
        </label>
        <input
          id="yak-trails-team-fill"
          type="range"
          min="0"
          max={DEFAULT_YAK_TRAILS_CONFIG.world.capacity - 1}
          value={teamFillTarget}
          onChange={(event) => setTeamFillTarget(Number(event.target.value))}
        />
        <small>Bots balance both sides and step aside as people join. Set 0 or 1 to wait for people only.</small>
        <fieldset className="yak-trails__lives-picker">
          <legend>Lives per player</legend>
          {[...DEFAULT_YAK_TRAILS_CONFIG.teams.suggestedLivesPerPlayer, null].map((lives) => (
            <button
              type="button"
              key={lives ?? "unlimited"}
              className={teamLives === lives ? "is-selected" : ""}
              onClick={() => setTeamLives(lives)}
            >
              {lives ?? "∞"}
            </button>
          ))}
        </fieldset>
        <div className="yak-trails__team-rules-note">
          <span>⌂</span>
          <p>Respawns use surviving territory on your original side. No safe team land means no respawn—even with lives left.</p>
        </div>
        {practiceEnabled && (
          <Button disabled={isStarting || teamFillTarget < DEFAULT_YAK_TRAILS_CONFIG.teams.minimumParticipants} onClick={() => void startLocalTeamBattle()}>
            {isStarting ? "Starting…" : `Battle bots${teamFillTarget >= DEFAULT_YAK_TRAILS_CONFIG.teams.minimumParticipants ? ` (${teamFillTarget - 1})` : ""}`}
          </Button>
        )}
        {onlineAvailable && (
          <div className="yak-trails__team-online-actions">
            <Button disabled={isStarting} variant="secondary" onClick={() => void startHostedTeamBattle("everyone")}>Play everyone</Button>
            <Button disabled={isStarting || !bootstrap.schoolAvailable} variant="secondary" onClick={() => void startHostedTeamBattle("school")}>My school</Button>
          </div>
        )}
      </div>
      {message && <p className="yak-trails__inline-error">{message}</p>}
    </div>
  );

  const tournamentScreen = (
    <div className="yak-trails__screen yak-trails__subscreen yak-trails__tournament-screen">
      <ScreenHeader title="Tournaments" onBack={showHome} />
      <div className="yak-trails__subscreen-intro yak-trails__tournament-intro">
        <span className="yak-trails__big-icon">🏆</span>
        <h3>Every trail counts.</h3>
        <p>Check in, enter your seeded heat, and advance by claiming territory. Formats range from one-life knockouts to timed territory rounds.</p>
        {bootstrap.tournamentAdminAvailable && (
          <button type="button" className="yak-trails__staff-link" onClick={() => {
            window.history.replaceState(null, "", "#/yak-trails/admin");
            setScreen("tournament-admin");
            track("tournament_admin_opened");
          }}>⚙ Manage tournaments</button>
        )}
      </div>
      {connectionStatus === "matching" && tournaments.length === 0 ? (
        <LoadingPill label="Loading brackets…" />
      ) : tournaments.length === 0 ? (
        <div className="yak-trails__tournament-empty">
          <strong>No open brackets</strong>
          <p>The next Yak Trails tournament will appear here when it is published.</p>
          <Button variant="secondary" onClick={() => void loadTournamentHub()}>Refresh</Button>
        </div>
      ) : (
        <div className="yak-trails__tournament-layout">
          <div className="yak-trails__tournament-tabs" role="list" aria-label="Available tournaments">
            {tournaments.map((tournament) => (
              <button
                type="button"
                role="listitem"
                key={tournament.id}
                className={tournament.id === selectedTournament?.id ? "is-selected" : ""}
                onClick={() => void selectTournament(tournament.id)}
              >
                <span className={`is-${tournament.status}`} />
                <b>{tournament.name}</b>
                <small>{tournament.status === "active" ? "LIVE" : tournamentStartLabel(tournament.startsAt)}</small>
              </button>
            ))}
          </div>
          {selectedTournament && (
            <section className="yak-trails__tournament-card" aria-label={`${selectedTournament.name} tournament`}>
              <header>
                <div>
                  <span className={`yak-trails__status-pill is-${selectedTournament.status}`}>
                    {selectedTournament.status === "active" ? "● LIVE NOW" : selectedTournament.status.toUpperCase()}
                  </span>
                  <h3>{selectedTournament.name}</h3>
                </div>
                <strong>{selectedTournament.mode === "team" ? "RED VS BLUE" : "FFA"}</strong>
              </header>
              <div className="yak-trails__tournament-facts">
                <span><b>START</b>{tournamentStartLabel(selectedTournament.startsAt)}</span>
                <span><b>POOL</b>{selectedTournament.scope === "school" ? "My school" : "Everyone"}</span>
                <span><b>RULE</b>{selectedTournamentStage?.respawnPolicy === "immediate"
                  ? "Timed • instant return"
                  : selectedTournamentStage?.respawnPolicy === "delayed"
                    ? "Timed • delayed return"
                    : "One-life heats"}</span>
              </div>
              <Button disabled={isStarting || ["paused", "cancelled", "completed"].includes(selectedTournament.status)} onClick={() => void enterTournament(selectedTournament)}>
                {isStarting
                  ? "Opening bracket…"
                  : selectedTournament.status === "active"
                    ? checkedInTournamentIds.has(selectedTournament.id) ? "Enter my heat" : "Check in & enter"
                    : checkedInTournamentIds.has(selectedTournament.id) ? "Checked in ✓" : "Check in"}
              </Button>
              <div className="yak-trails__tournament-standings">
                <div><strong>STANDINGS</strong><button type="button" onClick={() => void selectTournament(selectedTournament.id)}>Refresh</button></div>
                {tournamentStandings.length === 0 ? (
                  <p>No scores yet. The bracket is waiting for its first trail.</p>
                ) : tournamentStandings.slice(0, 5).map((standing, index) => (
                  <div key={standing.userId}>
                    <span>{standing.placement ?? index + 1}</span>
                    <b>{standing.alias}</b>
                    <strong>{standing.score.toFixed(1)}</strong>
                  </div>
                ))}
              </div>
              {tournamentBracket && tournamentBracket.tournamentId === selectedTournament.id && (
                <TournamentBracket
                  bracket={tournamentBracket}
                  currentPlayerId={bootstrap.profile?.userId}
                />
              )}
            </section>
          )}
        </div>
      )}
      {message && <p className="yak-trails__inline-error" role="status">{message}</p>}
      {tournamentNotice && <p className="yak-trails__tournament-notice" role="status">{tournamentNotice}</p>}
    </div>
  );

  const tournamentAdminScreen = (
    <TournamentAdminConsole
      sessionApi={sessionApi}
      onBack={() => {
        window.history.replaceState(null, "", "#/yak-trails");
        void loadTournamentHub();
      }}
      onChanged={() => track("tournament_admin_mutated")}
    />
  );

  const tutorialScreen = (
    <FirstRunTutorial
      onBack={showHome}
      onComplete={() => {
        writeTutorialComplete();
        setTutorialComplete(true);
        track("tutorial_completed");
        showHome();
      }}
      onStep={(step, action) => track("tutorial_step", { step: step + 1, action })}
    />
  );

  const partyScreen = (
    <div className="yak-trails__screen yak-trails__subscreen">
      <ScreenHeader title="Private Party" onBack={showHome} />
      <div className="yak-trails__subscreen-intro yak-trails__party-art">
        <span className="yak-trails__big-icon">✦</span>
        <h3>{party ? "Your arena is ready" : "A room for your crew"}</h3>
        <p>{party?.mode === "team" ? "One invite, balanced teams, and replayable Red vs Blue rounds." : "Friends can drop in, respawn, and keep the same arena going."}</p>
      </div>
      {party ? (
        <div className="yak-trails__party-card">
          <span className="yak-trails__party-label">INVITE CODE</span>
          <strong>{party.slug.toUpperCase()}</strong>
          <div className={classNames("yak-trails__party-mode-summary", party.mode === "team" && "is-team")}>
            <span>{party.mode === "team" ? "⚔️ RED VS BLUE" : "◎ FREE-FOR-ALL"}</span>
            <b>Round {party.round}</b>
            <small>
              {party.memberCount} player{party.memberCount === 1 ? "" : "s"} • fills toward {party.botFillTarget}
              {party.teamRules ? ` • ${party.teamRules.livesPerPlayer ?? "∞"} lives • first to ${party.teamRules.territoryVictoryPercent}%` : ""}
            </small>
          </div>
          <Button onClick={() => void copyPartyLink()}>Copy invite link</Button>
          {party.status === "between_rounds" ? (
            <Button disabled={isStarting} variant="secondary" onClick={replayTeamBattle}>
              {isStarting
                ? "Checking round…"
                : bootstrap.profile?.userId === party.hostPlayerId
                  ? "Start next round"
                  : "Check for next round"}
            </Button>
          ) : (
            <Button disabled={isStarting} variant="secondary" onClick={() => void joinPartyArena(party.slug)}>{isStarting ? "Entering…" : "Enter arena"}</Button>
          )}
          {bootstrap.profile?.userId === party.hostPlayerId && (
            <div className="yak-trails__host-controls">
              <span>HOST CONTROLS</span>
              <label htmlFor="yak-trails-host-bots">Fill target <strong>{botCount}</strong></label>
              <input id="yak-trails-host-bots" type="range" min="0" max={DEFAULT_YAK_TRAILS_CONFIG.world.capacity - 1} value={botCount} onChange={(event) => setBotCount(Number(event.target.value))} />
              <Button variant="secondary" onClick={() => void updatePartyBotFill()}>Update bots</Button>
              <button type="button" className="yak-trails__close-party" onClick={() => void closeParty()}>Close arena</button>
            </div>
          )}
        </div>
      ) : (
        <div className="yak-trails__party-card">
          <fieldset className="yak-trails__party-mode-picker">
            <legend>Arena mode</legend>
            <button type="button" className={partyMode === "ffa" ? "is-selected" : ""} onClick={() => setPartyMode("ffa")}>
              <span>◎</span><b>Free-for-all</b>
            </button>
            <button type="button" className={partyMode === "team" ? "is-selected" : ""} onClick={() => setPartyMode("team")}>
              <span>⚔️</span><b>Red vs Blue</b>
            </button>
          </fieldset>
          <label htmlFor="yak-trails-party-bots">Bot fill <strong>{botCount}</strong></label>
          <input id="yak-trails-party-bots" type="range" min="0" max={DEFAULT_YAK_TRAILS_CONFIG.world.capacity - 1} value={botCount} onChange={(event) => setBotCount(Number(event.target.value))} />
          {partyMode === "team" && (
            <fieldset className="yak-trails__lives-picker yak-trails__party-lives-picker">
              <legend>Lives per player</legend>
              {[...DEFAULT_YAK_TRAILS_CONFIG.teams.suggestedLivesPerPlayer, null].map((lives) => (
                <button
                  type="button"
                  key={lives ?? "unlimited"}
                  className={partyTeamLives === lives ? "is-selected" : ""}
                  onClick={() => setPartyTeamLives(lives)}
                >
                  {lives ?? "∞"}
                </button>
              ))}
            </fieldset>
          )}
          <small>{partyMode === "team" ? `Bots balance both sides. First team to ${DEFAULT_YAK_TRAILS_CONFIG.teams.territoryVictoryPercent}% or a full wipe wins.` : "Humans take bot spots without overfilling the arena."}</small>
          <Button disabled={isStarting} onClick={() => void createParty()}>{isStarting ? "Creating…" : "Create private arena"}</Button>
        </div>
      )}
      {connectionStatus === "matching" && <LoadingPill label="Opening your arena…" />}
      {message && <p className="yak-trails__inline-error">{message}</p>}
    </div>
  );

  const skinsScreen = (
    <div className="yak-trails__screen yak-trails__subscreen yak-trails__skins-screen">
      <ScreenHeader title="Voxel Pals" onBack={showHome} />
      <div className="yak-trails__skin-feature">
        <SkinShowcase skinId={selectedSkinId} />
        <div><small>EQUIPPED</small><strong>{skins.find((skin) => skin.id === selectedSkinId)?.name ?? "Yak"}</strong></div>
      </div>
      <div className="yak-trails__skins-grid">
        {skins.map((skin) => {
          const isUnlocked = unlocked.has(skin.id);
          const isSelected = skin.id === selectedSkinId;
          return (
            <button
              type="button"
              key={skin.id}
              className={classNames("yak-trails__skin-card", isSelected && "is-selected", !isUnlocked && "is-locked")}
              onClick={() => void selectSkin(skin)}
              style={{ "--skin-primary": skin.primaryColor, "--skin-secondary": skin.secondaryColor } as React.CSSProperties}
              aria-label={`${skin.name}${isUnlocked ? "" : `, unlocks at ${skin.unlockXp} XP`}`}
            >
              <span className="yak-trails__skin-cube"><i /><b /></span>
              <strong>{skin.name}</strong>
              <small>{isUnlocked ? (isSelected ? "Equipped" : "Available") : `🔒 ${skin.unlockXp} XP`}</small>
            </button>
          );
        })}
      </div>
      {message && <p className="yak-trails__inline-error">{message}</p>}
    </div>
  );

  const connecting = (
    <div className="yak-trails__screen yak-trails__connecting-screen">
      <div className="yak-trails__connecting-logo"><SkinShowcase skinId={selectedSkinId} /></div>
      <p className="yak-trails__eyebrow">YAK TRAILS</p>
      <h2>{connectionStatus === "bootstrapping" ? "Checking your trail pass" : "Finding fresh territory"}</h2>
      <LoadingPill label={connectionStatus === "bootstrapping" ? "Signing in…" : "Warming up the arena…"} />
      <button type="button" onClick={() => void cancelConnection()}>Cancel</button>
    </div>
  );

  const playing = snapshot && selfId ? (
    <div
      className={classNames(
        "yak-trails__play-surface",
        muted && "is-muted",
        isSpectating && "is-spectating",
        teamBattle && "is-team-battle",
      )}
      {...controls}
      role="application"
      aria-label="Yak Trails arena. Drag anywhere to steer."
    >
      <ArenaScene
        snapshot={snapshot}
        owners={territory.owners}
        playerId={selfId}
        cameraPlayerId={isSpectating ? spectatorTargetId : selfId}
        quality={quality}
        territoryWorkerUrl={territoryWorkerUrl}
        captureBurstTick={captureBurstTick}
        steeringDirection={steeringDirection}
        networkLatencyMs={sessionKind === "practice" ? 0 : networkLatencyMs}
      />
      {teamBattle && (
        <section className="yak-trails__team-scoreboard" aria-label="Team battle score">
          {teamBattle.teams.map((team) => (
            <div key={team.teamId} className={team.teamId === selfTeamId ? "is-self-team" : ""}>
              <span><i style={{ background: team.color }} />{team.teamId === "red" ? "RED" : "BLUE"}</span>
              <strong>{team.territoryPercent.toFixed(1)}%</strong>
              <small>{team.activePlayers}/{team.totalPlayers} up · {team.remainingLives === null ? "∞" : team.remainingLives} reserves</small>
            </div>
          ))}
          <b>FIRST TO {teamBattle.territoryVictoryPercent}%</b>
          <footer>
            <span>YOUR LAND <strong>{(selfPlayer?.territoryPercent ?? 0).toFixed(1)}%</strong></span>
            <span>PEAK <strong>{(selfPlayer?.peakTerritoryPercent ?? 0).toFixed(1)}%</strong></span>
            <span>CUTS <strong>{selfPlayer?.eliminations ?? 0}</strong></span>
          </footer>
        </section>
      )}
      {!teamBattle && (
        <div className="yak-trails__hud yak-trails__hud--stats">
          <span>TERRITORY</span>
          <strong>{(selfPlayer?.territoryPercent ?? 0).toFixed(1)}%</strong>
          <div><i style={{ width: `${Math.min(100, selfPlayer?.territoryPercent ?? 0)}%` }} /></div>
          <small>Peak {(selfPlayer?.peakTerritoryPercent ?? 0).toFixed(1)}% • ✦ {selfPlayer?.eliminations ?? 0}</small>
        </div>
      )}
      <div className={`yak-trails__network-chip is-${networkQuality}`}>
        <i /> {networkQuality === "good" ? "Live" : networkQuality === "degraded" ? "Slow" : "Offline"}
      </div>
      {tournamentSecondsRemaining !== null && (
        <div className="yak-trails__tournament-clock" role="timer" aria-label={`${tournamentSecondsRemaining} seconds remain in this heat`}>
          <span>HEAT</span>
          <strong>{Math.floor(tournamentSecondsRemaining / 60)}:{String(tournamentSecondsRemaining % 60).padStart(2, "0")}</strong>
        </div>
      )}
      <button type="button" className="yak-trails__menu-button" onClick={openMenu} aria-label="Open game menu">☰</button>
      <ol className="yak-trails__hud yak-trails__leaderboard" aria-label="Leaderboard">
        {snapshot.leaderboard.slice(0, 5).map((entry) => (
          <li key={entry.id} className={entry.id === selfId ? "is-self" : ""}>
            <span>{entry.rank}</span><i style={{ background: entry.color }} /><b>{entry.id === selfId ? "You" : entry.label}</b><small>{entry.territoryPercent.toFixed(1)}%</small>
          </li>
        ))}
      </ol>
      <MiniMap snapshot={snapshot} owners={territory.owners} playerId={selfId} />
      <div className="yak-trails__steer-hint">Drag to turn • release to cruise</div>
      {teamPreStart && teamBattle && (
        <section className="yak-trails__team-start-overlay" role="status" aria-live="polite">
          <div className="yak-trails__team-start-emblem"><span>R</span><b>VS</b><span>B</span></div>
          {teamBattle.status === "waiting" ? (
            <>
              <p className="yak-trails__eyebrow">TEAM LOBBY</p>
              <h2>Waiting for both sides</h2>
              <p>Red {teamBattle.teams.find((team) => team.teamId === "red")?.totalPlayers ?? 0} · Blue {teamBattle.teams.find((team) => team.teamId === "blue")?.totalPlayers ?? 0}</p>
            </>
          ) : (
            <>
              <p className="yak-trails__eyebrow">GET READY</p>
              <strong className="yak-trails__countdown-number">{teamCountdownSeconds}</strong>
              <p>Everyone starts together.</p>
            </>
          )}
          <button type="button" onClick={() => void leaveArena()}>Leave battle</button>
        </section>
      )}
      {spectatorActions && (
        <section className="yak-trails__spectator-controls" aria-label="Spectator controls">
          <div className="yak-trails__spectator-copy">
            <strong>SPECTATING</strong>
            <b>{spectatorTarget ? `${spectatorTarget.teamId === "red" ? "Red · " : spectatorTarget.teamId === "blue" ? "Blue · " : ""}${spectatorTarget.alias}` : "Waiting for a player"}</b>
            <small>{spectatorActions.detail}</small>
          </div>
          <div className="yak-trails__spectator-targets" aria-label="Change spectated player">
            <button
              type="button"
              aria-label="Previous player"
              onClick={() => setSpectatorTargetId(cycleSpectatorTargetId(snapshot, selfId, spectatorTargetId, "previous"))}
            >‹</button>
            <button
              type="button"
              aria-label="Next player"
              onClick={() => setSpectatorTargetId(cycleSpectatorTargetId(snapshot, selfId, spectatorTargetId, "next"))}
            >›</button>
          </div>
          <Button disabled={!spectatorActions.canRespawn} onClick={respawn}>
            {spectatorActions.buttonLabel}
          </Button>
          <button type="button" onClick={() => void (sessionKind === "tournament" ? returnToTournament() : leaveArena())}>
            {sessionKind === "tournament" ? "Bracket" : "Leave"}
          </button>
        </section>
      )}
      {connectionStatus === "reconnecting" && (
        <div className="yak-trails__reconnect" role="status">
          <span className="yak-trails__spinner" />
          <strong>Reconnecting</strong>
          <small>Your yak keeps moving • {reconnectSeconds}s left</small>
        </div>
      )}
      {reconnectFailed && (
        <div className="yak-trails__sheet-backdrop">
          <section className="yak-trails__pause-sheet" aria-label="Connection recovery">
            <span className="yak-trails__sheet-handle" />
            <p className="yak-trails__eyebrow">CONNECTION EXPIRED</p>
            <h2>Let’s get you back in</h2>
            <p>The old run ended safely. Rejoin a fresh spot or return home.</p>
            <Button disabled={isStarting} onClick={recoverHostedArena}>
              {isStarting ? "Rejoining…" : "Rejoin arena"}
            </Button>
            <button type="button" className="yak-trails__leave-button" onClick={() => void leaveArena()}>
              Leave to home
            </button>
          </section>
        </div>
      )}
      {teamFinished && teamBattle.winnerTeamId && (
        <div className="yak-trails__sheet-backdrop">
          <section className={classNames("yak-trails__result-sheet", "yak-trails__team-result", teamWon ? "is-victory" : "is-defeat")} aria-label="Team battle result">
            <div className="yak-trails__result-medal">{teamWon ? "🏆" : "🛡️"}</div>
            <p className="yak-trails__eyebrow">{teamWon ? "TEAM VICTORY" : "TEAM DEFEAT"}</p>
            <h2>{teamBattle.winnerTeamId === "red" ? "Red" : "Blue"} Team wins!</h2>
            <p className="yak-trails__team-result-reason">
              {teamBattle.victoryReason === "territory"
                ? `${teamBattle.territoryVictoryPercent}% territory target reached`
                : "The other team ran out of fighters and safe respawns"}
            </p>
            <div className="yak-trails__team-result-scores">
              {teamBattle.teams.map((team) => (
                <div key={team.teamId}>
                  <i style={{ background: team.color }} />
                  <span>{team.teamId === "red" ? "Red" : "Blue"}</span>
                  <strong>{team.territoryPercent.toFixed(1)}%</strong>
                </div>
              ))}
            </div>
            {result?.progressionEligible && <p className="yak-trails__team-xp">+{result.xpAwarded} XP from your final life</p>}
            {message && <p className="yak-trails__team-round-message" role="status">{message}</p>}
            <Button disabled={isStarting} onClick={replayTeamBattle}>
              {isStarting
                ? "Opening round…"
                : sessionKind === "party" && party?.hostPlayerId === bootstrap.profile?.userId
                  ? "Start next round"
                  : sessionKind === "party"
                    ? "Check next round"
                    : "Battle again"}
            </Button>
            <button type="button" className="yak-trails__leave-button" onClick={() => void leaveArena()}>Leave to home</button>
          </section>
        </div>
      )}
      {result && !teamFinished && (
        <div className="yak-trails__sheet-backdrop">
          <section className="yak-trails__result-sheet" aria-label="Run result">
            <div className="yak-trails__result-medal">{result.fullCapture ? "👑" : result.placement <= 3 ? "🏆" : "🌱"}</div>
            <p className="yak-trails__eyebrow">{sessionKind === "tournament" ? "HEAT COMPLETE" : "RUN COMPLETE"}</p>
            <h2>{result.fullCapture ? "Arena conquered!" : `Placed #${result.placement}`}</h2>
            <div className="yak-trails__result-grid">
              <div><strong>{result.finalTerritoryPercent.toFixed(1)}%</strong><small>Final</small></div>
              <div><strong>{result.peakTerritoryPercent.toFixed(1)}%</strong><small>Peak</small></div>
              <div><strong>{result.eliminations}</strong><small>Cuts</small></div>
              <div><strong>{result.progressionEligible ? `+${result.xpAwarded}` : "—"}</strong><small>{result.progressionEligible ? "XP" : "Unranked"}</small></div>
            </div>
            {unlockReveal && (
              <div className="yak-trails__unlock-reveal" role="status">
                <span>✨</span>
                <div><small>NEW SKIN UNLOCKED</small><strong>{unlockReveal.name}</strong></div>
              </div>
            )}
            <Button disabled={sessionKind !== "tournament" && snapshot.mode === "team" && !selfPlayer?.canRespawn} onClick={respawn}>
              {sessionKind === "tournament"
                ? "Return to bracket"
                : sessionKind === "ranked_solo"
                ? "Rematch"
                : snapshot.mode === "team" && !selfPlayer?.canRespawn
                  ? "No safe respawn"
                  : "Respawn"}
            </Button>
            <div className="yak-trails__result-secondary">
              {sessionKind !== "ranked_solo" && <button type="button" onClick={spectate}>Spectate</button>}
              <button type="button" onClick={() => void leaveArena()}>Leave</button>
            </div>
          </section>
        </div>
      )}
      {menuOpen && !result && !reconnectFailed && (
        <div className="yak-trails__sheet-backdrop">
          <section className="yak-trails__pause-sheet" aria-label="Game menu">
            <span className="yak-trails__sheet-handle" />
            <h2>{sessionKind === "practice" ? "Practice paused" : "Game menu"}</h2>
            <p>{sessionKind === "practice" ? "Your local checkpoint is safe." : "The online arena keeps moving while this menu is open."}</p>
            <Button onClick={closeMenu}>Keep playing</Button>
            <button type="button" className="yak-trails__sheet-row" onClick={() => {
              const feedback = ensureFeedback();
              feedback.setSoundEnabled(muted);
              if (muted) void feedback.unlock();
            }}><span>{muted ? "🔇" : "🔊"} Sound</span><strong>{muted ? "Off" : "On"}</strong></button>
            <button type="button" className="yak-trails__sheet-row" onClick={() => ensureFeedback().setHapticsEnabled(!feedbackPreferences.hapticsEnabled)}><span>📳 Haptics</span><strong>{feedbackPreferences.hapticsEnabled ? "On" : "Off"}</strong></button>
            <button type="button" className="yak-trails__sheet-row" onClick={() => setQuality((value) => value === "high" ? "low" : "high")}><span>✨ Graphics</span><strong>{quality === "high" ? "High" : "Battery"}</strong></button>
            <button type="button" className="yak-trails__leave-button" onClick={() => void leaveArena()}>Leave arena</button>
          </section>
        </div>
      )}
      {message && !result && !menuOpen && <button type="button" className="yak-trails__toast yak-trails__toast--play" onClick={() => setMessage(null)}>{message}</button>}
    </div>
  ) : connecting;

  return (
    <div
      ref={gameRootRef}
      className={classNames("yak-trails", className)}
      onPointerDownCapture={() => { void ensureFeedback().unlock(); }}
      onKeyDownCapture={() => { void ensureFeedback().unlock(); }}
    >
      <ConnectivityBanner />
      {screen === "home" && home}
      {screen === "practice" && practiceEnabled && practiceScreen}
      {screen === "online" && online}
      {screen === "team" && teamScreen}
      {screen === "tournaments" && tournamentScreen}
      {screen === "tournament-admin" && tournamentAdminScreen}
      {screen === "tutorial" && tutorialScreen}
      {screen === "party" && partyScreen}
      {screen === "skins" && skinsScreen}
      {screen === "connecting" && connecting}
      {screen === "playing" && playing}
    </div>
  );
}
